import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import type { RolloverPlan, RolloverResult } from '@lms/shared-types'
import { previewRollover, runRollover } from '../../api/leave-credits'
import { Card } from '../primitives/Card'
import { ApiError } from '../../api/client'

const CURRENT_YEAR = new Date().getFullYear()
const YEAR_OPTIONS = [1, 2, 3, 4, 5].map((offset) => CURRENT_YEAR + offset)

export function YearRolloverPanel(): JSX.Element {
  const [year, setYear] = useState<number>(CURRENT_YEAR + 1)
  const [plan, setPlan] = useState<RolloverPlan | null>(null)
  const [result, setResult] = useState<RolloverResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const previewMutation = useMutation({
    mutationFn: () => previewRollover({ year }),
    onSuccess: (data) => {
      setPlan(data)
      setResult(null)
      setError(null)
    },
    onError: (err) => {
      const status = err instanceof ApiError ? err.status : 500
      const msg = err instanceof Error ? err.message : 'Preview failed'
      setError(`${msg} (${status})`)
    },
  })

  const runMutation = useMutation({
    mutationFn: () => runRollover({ year }),
    onSuccess: (data) => {
      setResult(data)
      setError(null)
    },
    onError: (err) => {
      const status = err instanceof ApiError ? err.status : 500
      const msg = err instanceof Error ? err.message : 'Run failed'
      setError(`${msg} (${status})`)
    },
  })

  const planMatchesYear = plan?.year === year
  const runDisabled = !planMatchesYear || previewMutation.isPending || runMutation.isPending
  const showResultChip = result !== null && result.year === year

  const onYearChange = (next: number): void => {
    setYear(next)
    setPlan(null)
    setResult(null)
    setError(null)
  }

  return (
    <Card padding={20}>
      <header style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>
          Year-End Rollover
        </h2>
        <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-3)' }}>
          Materialize next year&rsquo;s balance rows for every employee. Idempotent — safe to
          re-click.
        </div>
      </header>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Target year</span>
          <select
            data-testid="rollover-year-picker"
            value={year}
            onChange={(e) => onYearChange(Number(e.target.value))}
            style={{
              padding: '6px 10px',
              borderRadius: 8,
              border: '1px solid var(--border)',
              background: 'var(--surface)',
              color: 'var(--text)',
              fontSize: 14,
            }}
          >
            {YEAR_OPTIONS.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            data-testid="rollover-preview-btn"
            onClick={() => previewMutation.mutate()}
            disabled={previewMutation.isPending}
            style={{
              padding: '8px 14px',
              borderRadius: 10,
              border: '1px solid var(--border)',
              background: 'var(--surface)',
              color: 'var(--text-2)',
              fontWeight: 600,
              fontSize: 14,
              cursor: previewMutation.isPending ? 'wait' : 'pointer',
              opacity: previewMutation.isPending ? 0.6 : 1,
            }}
          >
            {previewMutation.isPending ? 'Previewing…' : 'Preview'}
          </button>
          <button
            type="button"
            data-testid="rollover-run-btn"
            onClick={() => runMutation.mutate()}
            disabled={runDisabled}
            style={{
              padding: '8px 14px',
              borderRadius: 10,
              border: 'none',
              background: 'var(--steel-blue)',
              color: 'var(--surface)',
              fontWeight: 600,
              fontSize: 14,
              cursor: runDisabled ? 'not-allowed' : 'pointer',
              opacity: runDisabled ? 0.5 : 1,
            }}
          >
            {runMutation.isPending ? 'Running…' : 'Run rollover'}
          </button>
        </div>

        {error ? (
          <div
            role="alert"
            style={{
              padding: '10px 12px',
              borderRadius: 8,
              background: 'var(--danger-bg)',
              color: 'var(--danger-fg)',
              border: '1px solid var(--danger-line)',
              fontSize: 13,
            }}
          >
            {error}
          </div>
        ) : null}

        {plan && planMatchesYear && !result ? (
          <div
            style={{
              padding: 12,
              background: 'var(--surface-2)',
              borderRadius: 8,
              fontSize: 13,
            }}
          >
            <div>
              Employees processed:{' '}
              <strong data-testid="rollover-employees-processed">
                {plan.employeesProcessed}
              </strong>
            </div>
            <div>
              Rows to create:{' '}
              <strong data-testid="rollover-rows-to-create">{plan.rowsToCreate}</strong>
            </div>
            <div>
              Rows already exist:{' '}
              <strong data-testid="rollover-rows-already-exist">{plan.rowsAlreadyExist}</strong>
            </div>
            {plan.warnings.length > 0 ? (
              <ul style={{ marginTop: 8, paddingLeft: 18, color: 'var(--warning-fg)' }}>
                {plan.warnings.map((w, i) => (
                  <li key={i}>
                    {w.code}: {w.detail}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        {showResultChip && result ? (
          <div
            data-testid="rollover-result-chip"
            style={{
              padding: '8px 12px',
              borderRadius: 8,
              background: 'var(--success-bg)',
              color: 'var(--success-fg)',
              border: '1px solid var(--success-line)',
              fontSize: 13,
            }}
          >
            Rolled over {result.rowsCreated} rows for year {result.year}
            {result.rowsSkipped > 0 ? ` (${result.rowsSkipped} already existed)` : ''}.
          </div>
        ) : null}
      </div>
    </Card>
  )
}
